// ===========================================================================
// The channels a page watches, on whichever socket it holds.
//
// A page holds one socket: a session socket once it has a token, a visitor
// socket before that (visitor.js). Either carries the channels the page
// watches, 'age:<checkId>' while a check is reviewed and 'support:<requestId>'
// for a thread with an admin, and answers each with PUB frames the moment
// something changes. This is the bookkeeping between the two: what is
// watched, which socket carries it now, and the asking again when the socket
// changes.
//
// And the one place a request is still made: while NO socket carries the
// channels (the server's visitor cap, or an outage), a watcher may ask over
// HTTP instead, and stops the moment a socket is bound. Nothing here touches
// the DOM or config, so it is unit-tested under plain Node. ASCII only.
// ===========================================================================

import { ClientMsg } from '@lumi/shared';

/** After a failed ask: half a second, doubling, and no longer than this. */
export const ASK_BACKOFF_MAX_MS = 8000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function createChannels({ delay = sleep } = {}) {
  /** @type {Map<string, Set<(data: unknown) => void>>} channel -> watchers */
  const subs = new Map();
  /** @type {Set<(bound: boolean) => void>} told when a socket is bound or let go */
  const watchers = new Set();
  let socket = null;

  const say = (type, ch) => {
    if (socket) socket.send(type, { ch });
  };
  const isBound = () => !!socket && (typeof socket.isOpen !== 'function' || socket.isOpen());
  const tell = (bound) => {
    for (const fn of [...watchers]) {
      try {
        fn(bound);
      } catch {
        /* that watcher's problem */
      }
    }
  };

  /** A socket is the page's now: it is asked for everything watched. */
  function bind(sig) {
    socket = sig || null;
    if (!socket) return;
    for (const ch of subs.keys()) say(ClientMsg.SUB, ch);
    tell(isBound());
  }

  /** That socket is gone. Nothing is forgotten: the next bind asks again. */
  function unbind(sig) {
    if (!sig || socket !== sig) return;
    socket = null;
    tell(false);
  }

  /**
   * Watch a channel. The first watcher asks the socket; the last to leave
   * tells it. Returns the leaving; safe to call twice.
   */
  function subscribe(ch, fn) {
    if (typeof ch !== 'string' || !ch || typeof fn !== 'function') return () => {};
    let set = subs.get(ch);
    if (!set) {
      set = new Set();
      subs.set(ch, set);
      say(ClientMsg.SUB, ch);
    }
    set.add(fn);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const s = subs.get(ch);
      if (!s) return;
      s.delete(fn);
      if (!s.size) {
        subs.delete(ch);
        say(ClientMsg.UNSUB, ch);
      }
    };
  }

  /** A PUB frame arrived. One watcher failing must not starve the others. */
  function deliver(msg) {
    const set = msg && typeof msg.ch === 'string' ? subs.get(msg.ch) : null;
    if (!set) return 0;
    for (const fn of [...set]) {
      try {
        fn(msg.data);
      } catch {
        /* that watcher's problem; the rest still hear */
      }
    }
    return set.size;
  }

  /** Be told when a socket is bound (true) or let go (false). */
  function watch(fn) {
    watchers.add(fn);
    return () => watchers.delete(fn);
  }

  /**
   * Ask over HTTP, but only while no socket carries the channels, and again
   * whenever the socket goes. `ask` resolves to an answer, or null for a
   * failure; an answer goes to `onAnswer` at once and the next ask follows
   * without a pause (the route holds a request open itself), and only a
   * failure waits, doubling, so a broken connection never becomes a hot loop.
   * Returns the stopping.
   */
  function askWhileUnbound(ask, onAnswer) {
    let stopped = false;
    let running = false;
    let failures = 0;
    async function run() {
      if (running) return;
      running = true;
      try {
        while (!stopped && !isBound()) {
          let answer = null;
          try {
            answer = await ask();
          } catch {
            answer = null;
          }
          if (stopped) break;
          if (answer == null) {
            failures += 1;
            await delay(Math.min(500 * 2 ** (failures - 1), ASK_BACKOFF_MAX_MS));
            continue;
          }
          failures = 0;
          onAnswer(answer);
        }
      } finally {
        running = false;
      }
    }
    const unwatch = watch((bound) => {
      if (!bound) run();
    });
    if (!isBound()) run();
    return () => {
      stopped = true;
      unwatch();
    };
  }

  return { bind, unbind, subscribe, deliver, watch, isBound, askWhileUnbound, active: () => [...subs.keys()] };
}

/** The page's one set. */
export const channels = createChannels();

export default { createChannels, channels, ASK_BACKOFF_MAX_MS };
